import React, { useRef, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { ChromePicker } from 'react-color'
import PropTypes from 'prop-types'
import { Box, InputAdornment, Popper, TextField } from '@material-ui/core'

import regex from 'helpers/regex'

import useOutsideClick from 'hooks/useOutsideClick'
import usePermissions from 'hooks/usePermissions'

import styles from './styles'
import permissions from 'constants/permissions'

const ButtonColorPicker = ({ name, controlName, permission }) => {
  const [showPicker, setShowPicker] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)

  const boxChromePickerRef = useRef(null)
  const textFieldRef = useRef(null)

  const { control, errors, watch, setValue } = useFormContext()
  const { permitted } = usePermissions()

  const permissionToCheck =
    permission || permissions.PRIVACY_PORTAL_THEMES.MANAGE
  const isDisabled = !permitted(permissionToCheck)

  const colorPicker = watch(controlName)

  const useStyles = styles({ colorPicker })
  const classes = useStyles()

  const handleTextFieldFocus = (event) => {
    setAnchorEl(event.currentTarget)
    setShowPicker(true)
  }

  useOutsideClick({
    ref: boxChromePickerRef,
    callback: (event) => {
      if (textFieldRef.current && textFieldRef.current.contains(event.target)) {
        return
      }
      setShowPicker(false)
    },
  })

  const commonLetters = (value) => {
    const onlyLettersData = regex.onlyLetters(value)
    return regex.removeSpaces(onlyLettersData).toUpperCase().substring(0, 6)
  }

  const handleColorChange = (value) => '#' + commonLetters(value)

  const handleChromePickerChange = (color) => {
    const colorValue = handleColorChange(color)
    setValue(controlName, colorValue)
  }

  return (
    <Box>
      <Controller
        as={
          <TextField
            disabled={isDisabled}
            error={!!errors?.[controlName]}
            helperText={errors?.[controlName]?.message}
            label={name}
            variant="outlined"
            onFocus={handleTextFieldFocus}
            inputRef={textFieldRef}
            fullWidth
            value={colorPicker}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Box
                    className={classes.colorPreviewBox}
                    onClick={() => textFieldRef?.current?.focus()}
                  />
                </InputAdornment>
              ),
            }}
          />
        }
        onChange={([event]) => handleColorChange(event?.target?.value)}
        name={controlName}
        control={control}
      />
      <Popper
        open={showPicker}
        anchorEl={anchorEl}
        placement="bottom-end"
        className={classes.popper}
      >
        {showPicker && (
          <Box ref={boxChromePickerRef}>
            <ChromePicker
              color={colorPicker}
              disableAlpha
              onChange={(event) => handleChromePickerChange(event.hex)}
            />
          </Box>
        )}
      </Popper>
    </Box>
  )
}

ButtonColorPicker.propTypes = {
  name: PropTypes.string.isRequired,
  controlName: PropTypes.string.isRequired,
  permission: PropTypes.string,
}

export default ButtonColorPicker
